import { Component, computed, input, OnInit, output, signal, viewChild } from '@angular/core';
import { delay, shuffle } from '@gametailors/web-core/misc';
import { RidBottomButton } from '../../components/rid-bottom-button/rid-bottom-button';
import { RidKlankSwitchKlank } from '../../components/rid-klank-switch-klank/rid-klank-switch-klank';
import { RidMechanicBackground } from '../../components/rid-mechanic-background/rid-mechanic-background';
import { RidMechanicContent } from '../../components/rid-mechanic-content/rid-mechanic-content';
import { RidMechanicTitle } from '../../components/rid-mechanic-title/rid-mechanic-title';
import { RidMechanicWrapper } from '../../components/rid-mechanic-wrapper/rid-mechanic-wrapper';
import { RidOption, TOption } from '../../components/rid-option/rid-option';
import { RidOverlayFeedback } from '../../components/rid-overlay-feedback/rid-overlay-feedback';
import { RidTopBar } from '../../components/rid-top-bar/rid-top-bar';
import { RidSfxDirective } from '../../directives/rid-sfx.directive';
import { AudioEffectPlayer } from '../../utils/audio.utils';
import { KlankGenerator } from './rid-klank-switch.utils';

@Component({
  selector: 'rid-klank-switch',
  imports: [
    RidMechanicBackground,
    RidMechanicWrapper,
    RidTopBar,
    RidMechanicContent,
    RidMechanicTitle,
    RidOverlayFeedback,
    RidOption,
    RidBottomButton,
    RidKlankSwitchKlank,
    RidSfxDirective,
  ],
  templateUrl: './rid-klank-switch.html',
  host: {
    class: 'block',
  },
})
export class RidKlankSwitch implements OnInit {
  assignments = input.required<{ prompt: TOption; answer: string[]; shuffle: number }[]>();

  index = signal(0);
  assignment = computed(() => this.assignments()[this.index()]);
  progress = signal(0);
  item = viewChild.required<RidOption>('item');
  overlayFeedback = viewChild.required<RidOverlayFeedback>('overlayFeedback');
  text = signal<string[]>([]);
  isEvaluating = signal(false);
  showKlanken = signal(false);

  private generators: KlankGenerator[] = [];

  finished = output<void>();

  ngOnInit(): void {
    this.initialize(this.assignment());
  }

  initialize(assignment: { prompt: TOption; answer: string[]; shuffle: number }) {
    this.generators = assignment.answer.map((_, i) => new KlankGenerator(assignment.answer, i));
    const indicesToChange = shuffle(Array.from({ length: assignment.answer.length }, (_, i) => i)).slice(0, assignment.shuffle);
    this.text.set(assignment.answer.map((k, i) => (indicesToChange.includes(i) ? this.generators[i].getNext() : k)));
    this.showKlanken.set(false);
  }

  change(index: number) {
    if (this.isEvaluating()) return;

    const k = this.generators[index].getNext();
    AudioEffectPlayer.play('POP');
    this.text.update((text) => {
      const newText = [...text];
      newText[index] = k;
      return newText;
    });
  }

  async onItemPoppedIn() {
    await this.item().present();
    this.showKlanken.set(true);
  }

  check() {
    const isCorrect = this.assignment().answer.every((k, i) => k === this.text()[i]);
    const overlay = this.overlayFeedback();
    if (isCorrect) {
      this.markCorrect();
    } else {
      AudioEffectPlayer.play('INCORRECT');
      overlay.showIncorrect();
    }
  }

  async markCorrect() {
    this.isEvaluating.set(true);

    const isLast = this.index() === this.assignments().length - 1;

    if (isLast) {
      this.progress.update((p) => p + 1);
    }
    AudioEffectPlayer.play('CORRECT');
    this.overlayFeedback().showCorrect();
    await delay(1000);

    if (isLast) {
      this.finish();
    } else {
      this.progress.update((p) => p + 1);
      this.index.update((i) => i + 1);
      this.initialize(this.assignment());
      this.isEvaluating.set(false);
    }
  }

  finish() {
    this.finished.emit();
  }
}
